import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { HStack, Header, Text, VStack } from '@rbilabs/universal-components';
import { useStoreContext } from 'features/store';
import { useIsStoreInMobileOrderDriveThruStores } from 'features/store/hooks/use-is-store-in-mobile-order-drive-thru-stores';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { getStoreLocalTime } from 'features/store/utils';
import { useIntl } from 'react-intl';
import { ImageSourcePropType } from 'react-native';

import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logButtonClick,
  logEvent,
} from 'state/amplitude';
import { useCartContext } from 'state/cart';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ServiceMode } from 'state/order';
import { primitive } from 'styles/constants/primitives';
import { hapticSelection } from 'utils/haptic';
import { getHeadingForServiceMode } from 'utils/service-mode';

import { VehicleDescriptionMobilePickup } from '../payment/vehicle-description-mobile-pickup';

import { PickupSelector } from './pickup-selector';
import {
  ChangeServiceModeButton,
  DetailsWrapper,
  IconStyled,
  PickupDetailsBox,
  ServiceModesFooter,
} from './styled';
import { ICartDetailsProps } from './types';

const serviceModeImagesMap: Partial<Record<ServiceMode, ImageSourcePropType>> = {
  [ServiceMode.DRIVE_THRU]: require('./images/drive-thru.jpg'),
  [ServiceMode.MOBILE_ORDER_DRIVE_THRU]: require('./images/drive-thru.jpg'),
  [ServiceMode.TAKEOUT]: require('./images/take-out.jpg'),
  [ServiceMode.EAT_IN]: require('./images/dine-in.jpg'),
  [ServiceMode.CURBSIDE]: require('./images/curbside.jpg'),
};

const SERVICE_MODE_ORDER: ServiceMode[] = [
  ServiceMode.TAKEOUT,
  ServiceMode.DRIVE_THRU,
  ServiceMode.MOBILE_ORDER_DRIVE_THRU,
  ServiceMode.EAT_IN,
  ServiceMode.CURBSIDE,
];

const getServiceModeDescriptionTextId = (serviceMode: ServiceMode): string => {
  switch (serviceMode) {
    case ServiceMode.TAKEOUT:
      return 'serviceModeDetailsPickupDescriptionV2';
    case ServiceMode.CURBSIDE:
      return 'serviceModeDetailsCurbsideDescriptionV2';
    case ServiceMode.DRIVE_THRU:
      return 'serviceModeDetailsDriveThruDescriptionV2';
    case ServiceMode.MOBILE_ORDER_DRIVE_THRU:
      return 'serviceModeDetailsMobileOrderDriveThruDescriptionV2';
    case ServiceMode.EAT_IN:
      return 'serviceModeDetailsDineInDescriptionV2';
    default:
      return '';
  }
};

export const PickupDetails: React.FC<React.PropsWithChildren<
  Omit<ICartDetailsProps, 'serverOrder' | 'deliveryDetailsErrors'>
>> = ({ serviceMode: activeServiceMode }) => {
  const { store } = useStoreContext();
  const { selectServiceMode } = useCartContext();
  const isFastLaneEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAST_LANE_DRIVE_THRU);
  const isMobileOrderForDriveThruLaneEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU_LANE
  );
  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const {
    setVehicleDescription,
    vehicleDescription: { value: vehicleDescriptionValue, hasError: vehicleDescriptionHasError },
  } = useCartContext();

  const { formatMessage } = useIntl();
  const { getStoreServiceModeStatuses, isServiceModeAvailable } = useStoreAvailabilityFns();
  const [isServiceModeSelectorOpen, setIsServiceModeSelectorOpen] = useState(false);
  const isMobileOrderDriveThruEnabledForStore = useIsStoreInMobileOrderDriveThruStores(
    store.number
  );
  const isMobileOrderForDriveThru =
    (activeServiceMode === ServiceMode.MOBILE_ORDER_DRIVE_THRU &&
      isMobileOrderDriveThruEnabledForStore) ||
    (activeServiceMode === ServiceMode.DRIVE_THRU && isMobileOrderForDriveThruLaneEnabled);
  const serviceModeStatuses = getStoreServiceModeStatuses(store, getStoreLocalTime());

  const handleOpenServiceModeSelector = () => {
    logButtonClick(
      {
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          name: CustomEventNames.BUTTON_CLICK_SERVICE_MODE_WIDGET,
        },
      },
      { logDuplicateClickEvent: true }
    );

    setIsServiceModeSelectorOpen(true);
  };
  const getAvailableServiceModes = useCallback(() => {
    const availableServiceModes = SERVICE_MODE_ORDER.filter(serviceMode =>
      isServiceModeAvailable(store, serviceMode, getStoreLocalTime())
    );
    return availableServiceModes.flatMap(serviceMode => {
      const serviceModeHeadingAndIcon = getHeadingForServiceMode(
        formatMessage,
        isFastLaneEnabled,
        isMobileOrderForDriveThruLaneEnabled,
        serviceMode,
        false
      );

      const descriptionTextId = getServiceModeDescriptionTextId(serviceMode);
      return {
        ...serviceModeHeadingAndIcon,
        name: serviceMode,
        image: serviceModeImagesMap[serviceMode],
        description: formatMessage({ id: descriptionTextId }),
        isSelected: activeServiceMode === serviceMode,
      };
    });
  }, [
    isServiceModeAvailable,
    store,
    formatMessage,
    isFastLaneEnabled,
    isMobileOrderForDriveThruLaneEnabled,
    activeServiceMode,
  ]);

  const handleSelectServiceMode = useCallback(
    (mode: ServiceMode) => {
      const isUnavailable = !!serviceModeStatuses.find(sms => sms.serviceMode === mode)
        ?.featureFlagDisabled;
      if (isUnavailable) {
        return;
      }
      hapticSelection();
      selectServiceMode(mode);
      logEvent(CustomEventNames.BUTTON_CLICK_PICKUP, EventTypes.Other);
      setIsServiceModeSelectorOpen(false);
    },
    [serviceModeStatuses, selectServiceMode]
  );

  const availableServiceModes = useMemo(getAvailableServiceModes, [
    serviceModeStatuses,
    activeServiceMode,
    isFastLaneEnabled,
    isMobileOrderForDriveThruLaneEnabled,
    getAvailableServiceModes,
  ]);

  const selectedServiceMode = availableServiceModes.find(serviceMode => serviceMode.isSelected);
  const selectedServiceModeAccessibilityLabel = `${formatMessage({
    id: 'currentServiceMode',
  })}: ${selectedServiceMode?.heading}`;

  useEffect(() => {
    // After changing the restaurant, select the first available service mode if the previous one is not available
    if (!availableServiceModes.length) {
      return;
    }
    const selectedServiceModeNotAvailable = availableServiceModes.find(
      ({ isSelected }) => isSelected
    );

    if (!selectedServiceModeNotAvailable) {
      handleSelectServiceMode(availableServiceModes[0].name);
    }
  }, [availableServiceModes, handleSelectServiceMode]);

  return (
    <>
      <PickupDetailsBox>
        <DetailsWrapper>
          <HStack flex={1}>
            <IconStyled variant={selectedServiceMode?.icon} />
            <VStack flex={1}>
              <Header
                fontSize="md"
                lineHeight="sm"
                marginBottom="$1"
                accessible
                accessibilityRole="text"
                focusable
                accessibilityLabel={selectedServiceModeAccessibilityLabel}
              >
                {selectedServiceMode?.heading}
              </Header>
              <Text lineHeight="sm" fontSize="sm" numberOfLines={2} marginRight="$1">
                {selectedServiceMode?.description}
              </Text>
            </VStack>
          </HStack>
          <ChangeServiceModeButton
            _text={{ fontSize: 'xs', lineHeight: 12 }}
            variant="outline"
            onPress={handleOpenServiceModeSelector}
            isDesktopWeb={isDesktopWeb}
            testID="change-service-mode-button"
            accessibilityLabel={formatMessage({ id: 'changeServiceMode' })}
          >
            {formatMessage({ id: 'change' })}
          </ChangeServiceModeButton>
        </DetailsWrapper>
        <ServiceModesFooter>
          <Text
            testID="available-service-modes-text"
            color={primitive.$blackOpacity70}
            fontSize="2xs"
          >
            {availableServiceModes.map(serviceMode => serviceMode.heading).join(', ')}
          </Text>
        </ServiceModesFooter>
      </PickupDetailsBox>

      {isMobileOrderForDriveThru && (
        <VehicleDescriptionMobilePickup
          onChange={setVehicleDescription}
          value={vehicleDescriptionValue}
          hasError={vehicleDescriptionHasError}
        />
      )}
      <PickupSelector
        isOpen={isServiceModeSelectorOpen}
        onClose={() => setIsServiceModeSelectorOpen(false)}
        availableServiceModes={availableServiceModes}
        handleSelectServiceMode={handleSelectServiceMode}
      />
    </>
  );
};
